import {
  auth,
  FirebaseAuthTypes,
  logger,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  SMSInputScreen,
  useAlert,
  useTheme,
} from "capsule"
import React, { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"
import { Text } from "react-native-paper"

import { INPUT_HEIGHT, SMS_CODE_LENGTH } from "../features/config/Constants"
import { LoginNS } from "../features/i18n/constants"
import { useMfaResolver } from "../features/Providers/MfaProvider"
import crashlytics from "../services/Analytics/crashlytics"

interface IProps {
  verifId: string
  onSuccess?: () => Promise<void>
}

const MfaSmsValidationForm: FC<IProps> = ({ verifId, onSuccess }) => {
  const [verificationId, setVerificationId] = useState(verifId)
  const {
    dimensions: { spacing },
    colors: {
      surface: { background, textInput },
    },
  } = useTheme()
  const { t } = useTranslation(LoginNS)
  const [editable, setEditable] = useState(true)
  const { showSnack } = useAlert()

  const { resolver } = useMfaResolver()

  const { view, inputStyle } = useMemo(
    () => ({
      view: [
        styles.view,
        { backgroundColor: background, paddingHorizontal: spacing, paddingVertical: spacing * 2 },
      ],
      inputStyle: { backgroundColor: textInput, minHeight: INPUT_HEIGHT },
    }),
    [background, spacing, textInput],
  )
  const onValidate = useCallback(
    async (code: string) => {
      setEditable(false)
      try {
        if (code.length !== SMS_CODE_LENGTH || !resolver) {
          return
        }
        if (!verificationId) {
          showSnack({ message: t("errors.mfaError") })
          return
        }
        const credential = PhoneAuthProvider.credential(verificationId, code)

        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential)

        await resolver.resolveSignIn(multiFactorAssertion)
        if (onSuccess) {
          await onSuccess()
        }
      } catch (e) {
        crashlytics.reportError(10, `confirmation code error : ${e.message}`)
        logger("Confirmation code error: ", e)
        switch (e.code) {
          case "auth/invalid-verification-code":
            showSnack({ message: t("errors.invalidCode") })
            break
          default:
            showSnack({ message: t("errors.mfaError") })
        }
      } finally {
        setEditable(true)
      }
    },
    [resolver, verificationId, onSuccess, showSnack, t],
  )
  const onResend = useCallback(async () => {
    setEditable(false)
    if (!resolver) {
      setEditable(true)
      return
    }
    try {
      if (resolver.hints.length === 0) {
        logger("Resolver hints is empty")
        showSnack({ message: t("errors.unknown") })
        return
      }
      const hint = resolver.hints[0]

      if (hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) {
        return
      }

      const newVerificationId = await new PhoneAuthProvider(auth()).verifyPhoneNumber({
        multiFactorHint: hint,
        session: resolver.session,
      })

      setVerificationId(newVerificationId)
    } catch (e) {
      crashlytics.reportError(10, `resend code error : ${e.message}`)
      logger("Resend code error: ", e)
      showSnack({ message: t("errors.unknown") })
    } finally {
      setEditable(true)
    }
  }, [resolver, showSnack, t])

  return (
    <View style={view}>
      <Text style={styles.title}>{t("smsCode")}</Text>
      <SMSInputScreen
        phone={(resolver?.hints[0] as FirebaseAuthTypes.PhoneMultiFactorInfo)?.phoneNumber ?? ""}
        {...{ onValidate, inputStyle, length: SMS_CODE_LENGTH, editable, onResend }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
  title: {
    fontWeight: "bold",
    fontSize: 14,
  },
})

export default MfaSmsValidationForm
